import { useMemo } from "react"
import {
  Avatar,
  Box,
  Card,
  CardMedia,
  Chip,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material"
import clsx from "clsx"
import { deepmerge } from "@mui/utils"
import get from "lodash/get"
import { getFeatureDetail, IOccupantDetail } from "utils/feature"
import { useTranslation } from "providers/i18n"
import { CustomIcon, ShopIcon } from "components/icons"
import { ComingSoonChip, NewShopChip } from "../../chips"
import { Link } from "react-router-dom"
import { ImdfFeature } from "providers/venue/types"

export interface IVerticalDirectoryOccupantCardProps {
  data: ImdfFeature
  sx?: SxProps
  endAdornmentSlotClasses?: { [k: string]: any }
  endAdornmentSlots?: { [k: string]: any }
  isNewShop?: boolean
  isComingSoon?: boolean
}

export const VerticalDirectoryOccupantCard: React.FC<
  IVerticalDirectoryOccupantCardProps
> = ({
  data,
  sx,
  endAdornmentSlotClasses,
  endAdornmentSlots,
  isNewShop = false,
  isComingSoon = false,
  ...props
}) => {
  const { t } = useTranslation()
  const categoryIcon = useMemo(
    () => get(data, "properties.local_categories[0].properties.icon_path"),
    [data]
  )
  const {
    name,
    logo,
    level,
    venueLogo,
    group,
    category,
    boothName,
    isAd,
    cardStyle,
  } = useMemo(() => getFeatureDetail(data) as IOccupantDetail, [data])

  const theme = useTheme()
  const { styleOverrides = {} } =
    theme.components?.[`VerticalDirectoryOccupantCard`] || {}
  const {
    root,
    occupantLogo,
    cardContent,
    levelAvatar,
    venueLogo: venueLogoStyle,
    titleText,
    boothText,
    groupText,
    detailWrapper,
    infoContainer,
    categoryText,
    chipContainer,
    adsChip,
    newShopChip,
    comingSoonChip,
  } = styleOverrides

  return (
    <Card
      component={Link}
      to={`/maps/place/${data.id}`}
      sx={deepmerge(root, sx)}
      {...props}
      className={clsx(
        {
          comingSoon: isComingSoon,
        },
        cardStyle
      )}
    >
      <Box sx={occupantLogo}>
        <Box className="DirectoryOccupantCard-chipContainer" sx={chipContainer}>
          {isAd && (
            <Chip
              className="DirectoryOccupantCard-adsChip"
              label={t("sponsored.search_result")}
              sx={adsChip}
            />
          )}
          {isNewShop && !isAd && (
            <NewShopChip
              className="DirectoryOccupantCard-newShopChip"
              sx={newShopChip}
            />
          )}
          {isComingSoon && !isAd && (
            <ComingSoonChip
              className="DirectoryOccupantCard-comingSoonChip"
              sx={comingSoonChip}
            />
          )}
        </Box>
        <Avatar src={logo} className="occupant-logo-avatar">
          {!logo &&
            (categoryIcon ? <CustomIcon path={categoryIcon} /> : <ShopIcon />)}
        </Avatar>
      </Box>
      <Box sx={cardContent}>
        <Typography sx={titleText}>{t(name)}</Typography>
        {boothName && (
          <Typography sx={boothText}>
            {`${t("Booth")} ${t(boothName)}`}
          </Typography>
        )}
        <Typography variant="body2" color="grey" sx={groupText}>
          {t(group)}
        </Typography>
        <Typography sx={categoryText}>{t(category)}</Typography>
        <Box sx={detailWrapper}>
          <Box sx={infoContainer}>
            <Avatar sx={levelAvatar}>{t(level)}</Avatar>
            <CardMedia component="img" image={venueLogo} sx={venueLogoStyle} />
          </Box>
        </Box>
      </Box>
    </Card>
  )
}
